/**
 * offlineQueue
 * ────────────
 * IndexedDB-backed FIFO queue for backend operations that need to
 * survive an offline gap (truck in a canyon, basement of a building,
 * elevator in a hospital, hike-in remote site, etc.).
 *
 * What we queue:
 *   • `tech-location`     — GPS pings while clocked in (recorded locally,
 *                           flushed when signal returns).
 *   • `dispatch-status`   — Clock-in/out, en-route/at-site transitions.
 *   • `wo-status`         — Parent WO status mirror for the same flips.
 *
 * Why IndexedDB and not localStorage?
 *   • localStorage is synchronous and clears on private-tab cleanup;
 *     IndexedDB persists across browser restarts and isn't blocked by
 *     iOS Safari's intelligent-tracking-prevention timers.
 *   • Each ping is ~120 bytes — even a full off-grid 8-hour shift at
 *     60-second cadence is < 60KB. We pad the storage budget at 5 MB
 *     before we'd auto-prune the oldest pings.
 *
 * Auto-flush strategy:
 *   • `window.online` event → drains the queue.
 *   • Also: every 30 seconds while the queue is non-empty, attempt
 *     a flush (handles flaky cell where `online` doesn't fire reliably).
 *
 * Failure handling:
 *   • Each item tracks `attempts` and `last_error`. After 8 failed
 *     attempts within an hour, we mark the item `dead` so it stops
 *     blocking the queue; a future "Show offline diagnostics" UI can
 *     surface the dead items to support without losing data.
 */
import axios from "axios";

const DB_NAME = "serviceops_offline";
const DB_VERSION = 1;
const STORE = "queue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let _db = null;
let _flushTimer = null;
let _flushInFlight = false;
const _listeners = new Set();

// #126 (Group G): each queued record is tagged with the user id that
// enqueued it. On flush we ONLY drain records belonging to the
// currently-signed-in user, and `clearForLogout()` (called from
// AuthContext.logout) drops everything so the next user on the same
// device doesn't accidentally fire someone else's pending GPS pings.
// `setCurrentUserId(null)` ALSO suppresses the flush loop until a
// session is re-established.
let _currentUserId = null;
export function setCurrentUserId(id) { _currentUserId = id || null; }
export function getCurrentUserId() { return _currentUserId; }

export async function clearForLogout() {
  // Wipe ALL queued records on logout. We don't try to preserve them
  // for "the same user logs back in" because there is no way to tell
  // from a queued record alone whether the original session is the
  // same person — and stale GPS pings posted under a wrong session
  // are worse than dropping the queue.
  try {
    await withStore("readwrite", store => store.clear());
    emit();
  } catch { /* best effort */ }
  setCurrentUserId(null);
}

function emit() {
  _listeners.forEach(fn => { try { fn(); } catch { /* ignore */ } });
}
export function subscribe(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

function openDB() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("IndexedDB unavailable"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const s = db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
        s.createIndex("created_at", "created_at");
        s.createIndex("kind", "kind");
      }
    };
    req.onsuccess = () => { _db = req.result; resolve(_db); };
    req.onerror = () => reject(req.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    // #(med) (iter168): IDBObjectStore methods like .add/.put/.get/.delete
    // return IDBRequest objects, NOT their result values. Listen on the
    // request's onsuccess so callers receive the actual result (e.g.
    // the generated keypath id from .add). Falls back to resolving with
    // the raw return value when the callback didn't produce a request.
    let resolved = false;
    const result = fn(store);
    if (result && typeof result.onsuccess !== "undefined") {
      result.onsuccess = () => {
        if (!resolved) { resolved = true; resolve(result.result); }
      };
      result.onerror = () => reject(result.error);
    }
    tx.oncomplete = () => {
      if (!resolved) { resolved = true; resolve(result?._unwrap ?? result); }
    };
    tx.onerror = () => reject(tx.error);
  });
}

export async function enqueue(kind, payload) {
  const item = {
    kind,
    payload,
    attempts: 0,
    last_error: null,
    created_at: new Date().toISOString(),
    dead: false,
    // #126 (Group G): tag with current user id so a later flush only
    // sends records that belong to whoever is signed in NOW.
    user_id: _currentUserId,
  };
  await withStore("readwrite", store => store.add(item));
  emit();
  // Fire-and-forget flush attempt — works whenever the network is up.
  schedulePeriodicFlush();
  flush().catch(() => {});
  return item;
}

export async function pendingCount() {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, "readonly");
    const idx = tx.objectStore(STORE).index("kind");
    const req = idx.count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(0);
  });
}

export async function pendingList() {
  const db = await openDB();
  return new Promise((resolve) => {
    const items = [];
    const tx = db.transaction(STORE, "readonly");
    const cursor = tx.objectStore(STORE).openCursor();
    cursor.onsuccess = (e) => {
      const c = e.target.result;
      if (c) { items.push(c.value); c.continue(); }
      else resolve(items);
    };
    cursor.onerror = () => resolve([]);
  });
}

// Endpoint mapping for each kind. We use axios directly (NOT the shared
// API client) so we don't interfere with its 401-retry interceptor, and
// we explicitly DON'T re-queue an item after a 4xx (those are permanent
// failures — bad payload, deleted dispatch, etc.).
const ENDPOINTS = {
  "tech-location":    { method: "post", url: () => `${BACKEND_URL}/api/dispatch/tech-location` },
  "dispatch-status":  { method: "post", url: (p) => `${BACKEND_URL}/api/dispatches/${p.dispatch_id}/update-status` },
  "wo-status":        { method: "post", url: () => `${BACKEND_URL}/api/dispatch/update-status` },
};

async function attempt(item) {
  const spec = ENDPOINTS[item.kind];
  if (!spec) throw new Error(`unknown kind ${item.kind}`);
  // The dispatch_id is part of the URL for dispatch-status; strip it
  // before sending the body to keep payload schemas tight.
  let body = { ...item.payload };
  if (item.kind === "dispatch-status") delete body.dispatch_id;
  await axios({
    method: spec.method,
    url: spec.url(item.payload),
    data: body,
    withCredentials: true,
    timeout: 10000,
  });
}

export async function flush() {
  if (_flushInFlight) return;
  // #(med) (iter168): take the in-flight lock IMMEDIATELY after the
  // re-entry guard. The previous version set `_flushInFlight = true`
  // AFTER the onLine + userId checks, so two of those branches could
  // early-exit without ever releasing the lock — leaving the queue
  // stuck "in flight" forever.
  _flushInFlight = true;
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    _flushInFlight = false;
    return;
  }
  // #126 (Group G): the previous version of this function tried to
  // drain EVERY queued record regardless of which user originally
  // enqueued it. With multiple users sharing a phone (or a fresh
  // login after a stranded queue), that posted GPS pings / status
  // changes under the wrong identity. Now: if nobody's signed in we
  // bail; we only attempt records whose `user_id` matches the
  // currently-signed-in session. Untagged legacy records are
  // dropped (better than firing them under an unknown identity).
  if (!_currentUserId) {
    _flushInFlight = false;
    return;
  }
  try {
    const items = await pendingList();
    // Process oldest first. Stop on the first network failure so we
    // preserve order — a dispatch's clock-in must land before the
    // clock-out that immediately follows it.
    for (const item of items.sort((a, b) => a.id - b.id)) {
      if (item.dead) continue;
      if (!item.user_id) {
        // Legacy untagged record — drop it. Replaying without an
        // owner is exactly what got us into trouble.
        await withStore("readwrite", store => store.delete(item.id));
        emit();
        continue;
      }
      if (item.user_id !== _currentUserId) {
        // Belongs to a different user; leave it in IndexedDB so that
        // user can drain it the next time they sign in on this device
        // (assuming they do so before logout-clear runs). Skip without
        // breaking the loop so subsequent records still get a chance.
        continue;
      }
      try {
        await attempt(item);
        await withStore("readwrite", store => store.delete(item.id));
        emit();
      } catch (err) {
        const code = err?.response?.status;
        const isPermanent = code && code >= 400 && code < 500 && code !== 408 && code !== 429;
        const attempts = (item.attempts || 0) + 1;
        const updated = {
          ...item,
          attempts,
          last_error: err?.response?.data?.detail || err?.message || "unknown",
          dead: isPermanent || attempts >= 8,
        };
        await withStore("readwrite", store => store.put(updated));
        emit();
        // Stop the loop on transient network failure — wait for next online tick.
        if (!isPermanent) break;
      }
    }
  } finally {
    _flushInFlight = false;
  }
}

function schedulePeriodicFlush() {
  if (_flushTimer) return;
  _flushTimer = setInterval(async () => {
    const n = await pendingCount();
    if (n === 0) { clearInterval(_flushTimer); _flushTimer = null; return; }
    flush().catch(() => {});
  }, 30000);
}

// Auto-install global listeners exactly once.
if (typeof window !== "undefined" && !window.__offlineQueueWired) {
  window.__offlineQueueWired = true;
  window.addEventListener("online", () => flush().catch(() => {}));
  // Some browsers fire `online` before connection is actually usable;
  // a small delay before the first attempt gives DNS time to resolve.
  window.addEventListener("focus", () => setTimeout(() => flush().catch(() => {}), 250));
  // Kick a flush on load so unsent items from the last session don't sit.
  setTimeout(() => flush().catch(() => {}), 1000);
  schedulePeriodicFlush();
}
